import { FC, Fragment, useMemo } from "react";
import {
  Button,
  Link,
  Popover,
  PopoverOrigin,
  Stack,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  NullableFilterTreeNode,
  useGetUsersMeOrganisationsCurrentResourceExplorerPossibleFiltersQuery,
} from "@/services/cloudchipr.api";
import { useAppSelector } from "@/store/hooks";
import { resourceExplorerViewsHierarchyDefaultViewSelector } from "@/store/resource-explorer/selectors/views-hierarchy/resourceExplorerViewsHierarchyDefaultViewSelector";
import { calculateFilterTreeFiltersCount } from "@/components/pages/common/filters-tree/utils/helpers/calculateFilterTreeFiltersCount";
import { addIdsToFilterTreeItems } from "@/components/pages/common/filters-tree/utils/helpers/ids-manipulations/addIdsToFilterTreeItems";
import { useMenuHook } from "@/utils/hooks/useMenu.hook";
import { FilterTreeDisabledView } from "@/components/pages/common/filters-tree/filters-disabled-view/FilterTreeDisabledView";
import { isTreeNodeFilterGroup } from "@/components/pages/common/filters-tree/utils/helpers/isTreeNodeWithIdFilterGroup";

interface BudgetScopeFiltersChipProps {
  budgetId: string;
  openInNew?: boolean;
  filterTree: NullableFilterTreeNode;
}

export const BudgetScopeFiltersChip: FC<BudgetScopeFiltersChipProps> = ({
  budgetId,
  openInNew,
  filterTree,
}) => {
  const { open, openMenu, closeMenu, anchor } = useMenuHook();
  const { data, isLoading } =
    useGetUsersMeOrganisationsCurrentResourceExplorerPossibleFiltersQuery({});

  const defaultBillingReportId = useAppSelector(
    resourceExplorerViewsHierarchyDefaultViewSelector,
  );

  const url = defaultBillingReportId
    ? `/resource-explorer/${defaultBillingReportId}?budgetId=${budgetId}`
    : "#";

  const filtersCount = useMemo(() => {
    if (!filterTree) {
      return;
    }
    return calculateFilterTreeFiltersCount(addIdsToFilterTreeItems(filterTree));
  }, [filterTree]);

  return (
    <Fragment>
      <Stack
        data-block-row-click
        pr={1}
        pl={0.5}
        spacing={0.5}
        direction="row"
        sx={stackStyles}
        borderRadius={1}
        onClick={openMenu}
        component={Button}
        disabled={isLoading}
        bgcolor="primary.light"
        py={openInNew ? 0 : 0.5}
      >
        <FilterAltOutlinedIcon fontSize="small" />
        <Typography
          variant="body2"
          sx={{ textDecoration: "underline", textUnderlineOffset: 4 }}
        >
          Filters{!!filtersCount && ` (${filtersCount})`}
        </Typography>
      </Stack>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={closeMenu}
        slotProps={slotProps}
        anchorOrigin={anchorOrigin}
      >
        <Stack maxHeight="70dvh">
          <Stack p={1.5}>
            {data && isTreeNodeFilterGroup(filterTree) && (
              <FilterTreeDisabledView
                filterTree={filterTree}
                possibleFilters={data}
              />
            )}
          </Stack>

          <Stack
            py={1}
            px={1.5}
            bottom={0}
            spacing={1}
            bgcolor="white"
            borderTop={1}
            borderColor="grey.300"
            direction="row"
            position="sticky"
            alignItems="center"
          >
            <Typography variant="body2" sx={{ textDecoration: "none" }}>
              Open in Billing Explorer
            </Typography>
            <Stack component={Link} target="_blank" href={url}>
              <LaunchIcon sx={{ fontSize: 16 }} color="primary" />
            </Stack>
          </Stack>
        </Stack>
      </Popover>
    </Fragment>
  );
};

const slotProps = {
  paper: { sx: { p: 0 } },
};

const anchorOrigin: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "left",
};

const stackStyles = {
  "&:hover": {
    bgcolor: "primary.light",
    opacity: 0.9,
  },
};
